import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from '../config/baseUrl';

// Calendar Icon
const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

// Swap Right Icon
const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

// Fetch casino list
const fetchCasinoList = async function () {
  try {
    const token = getAccessToken();

    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/casino/casino-list",
      {},
      { headers: { Authorization: "Bearer " + token } },
    );

    const data = response.data;

    if (data.status === false) {
      return {
        success: false,
        error: data.message || "Failed to fetch casino list",
      };
    }

    return { success: true, data: data.data || [] };
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || "Failed to fetch casino list";
    return { success: false, error: errorMessage };
  }
};

// Get current date/time in format: DD-MM-YYYY HH:MM:SS AM/PM
const getCurrentDateTime = function () {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();

  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");

  return (
    day +
    "-" +
    month +
    "-" +
    year +
    " " +
    hoursStr +
    ":" +
    minutes +
    ":" +
    seconds +
    " " +
    ampm
  );
};

const CasinoMatchLedgerPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [casinoList, setCasinoList] = useState([]);

  // Fetch casino list on mount
  useEffect(function () {
    const loadCasinoList = async function () {
      setLoading(true);
      setError(null);

      const result = await fetchCasinoList();

      if (result.success) {
        setCasinoList(result.data);
      } else {
        setError(result.error);
        setCasinoList([]);
      }

      setLoading(false);
    };

    loadCasinoList();
  }, []);

  const handleView = function(tableId, name) {
    console.log('View:', tableId, name);
    // Navigate to the casino view page
    navigate('/casino/view/' + tableId + '/' + encodeURIComponent(name));
  };

  const handleDisplayGames = function (name, tableId) {
    console.log("Display Games:", name, tableId);
    navigate(
      "/casino/display-games/" + tableId + "/" + encodeURIComponent(name),
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-7xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-2 flex items-center justify-between">
              <span className="text-2xl text-white font-normal capitalize">
                Match Ledger
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Filters - Responsive */}
            <div className="mt-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center px-5 gap-3 mb-3">
                {/* Date Range Picker - Responsive */}
                <div className="flex-1 w-full sm:max-w-md">
                  <div
                    className="flex items-center border border-gray-300 bg-white w-full"
                    style={{ borderRadius: 0 }}
                  >
                    <div className="flex-1 px-2 py-2 min-w-0">
                      <input
                        type="text"
                        readOnly
                        placeholder="Start date"
                        className="w-full text-sm focus:outline-none bg-transparent"
                        style={{ fontSize: "12px" }}
                      />
                    </div>
                    <div className="px-2 text-gray-400 flex-shrink-0">
                      <SwapRightIcon />
                    </div>
                    <div className="flex-1 px-2 py-2 min-w-0">
                      <input
                        type="text"
                        readOnly
                        placeholder="End date"
                        className="w-full text-sm focus:outline-none bg-transparent"
                        style={{ fontSize: "12px" }}
                      />
                    </div>
                    <div className="px-2 text-gray-400 flex-shrink-0">
                      <CalendarIcon />
                    </div>
                  </div>
                </div>

                {/* Submit Button - Responsive */}
                <div className="w-full sm:w-auto">
                  <button
                    className="w-full sm:w-auto bg-blue-500 text-white px-6 py-2 hover:bg-blue-600"
                    style={{ borderRadius: 0 }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Code
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Name
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Date &amp; Time
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {casinoList.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          No casino games found
                        </td>
                      </tr>
                    ) : (
                      casinoList.map(function (casino, index) {
                        return (
                          <tr key={casino.tableId} className="hover:bg-gray-50">
                            <td className="p-2 border border-gray-300">
                              {index + 1}
                            </td>
                            <td className="p-2 border border-gray-300">
                              <button
                                onClick={function () {
                                  handleView(casino.tableId, casino.name);
                                }}
                                className="text-blue-500 hover:text-blue-700 hover:underline"
                              >
                                {casino.name}
                              </button>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap">
                                {getCurrentDateTime()}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <div className="flex flex-col sm:flex-row justify-end gap-2">
                                <button
                                  onClick={function () {
                                    handleView(casino.tableId, casino.name);
                                  }}
                                  className="bg-blue-500 text-white px-3 py-1 hover:bg-blue-600"
                                  style={{ borderRadius: 0 }}
                                >
                                  View
                                </button>
                                <button
                                  onClick={function () {
                                    handleDisplayGames(
                                      casino.name,
                                      casino.tableId,
                                    );
                                  }}
                                  className="bg-white text-gray-700 border border-gray-300 px-3 py-1 hover:bg-gray-100"
                                  style={{ borderRadius: 0 }}
                                >
                                  Display Games
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoMatchLedgerPage;